import React, { useState, useCallback } from "react";
import { menuItems as originalMenuItems, MenuItem } from "../../data/menuData";
import {
  menuConfigs as originalMenuConfigs,
  submenuLabels as originalSubmenuLabels,
  submenuAddOns as originalSubmenuAddOns,
  submenuInfos as originalSubmenuInfos,
} from "../../data/menuConfig";
import { recipeBook as originalRecipeBook } from "../../data/recipes";
import type { Recipe } from "../../data/recipes/types";
import MenuItemsManager from "./MenuItemsManager";
import RecipesManager from "./RecipesManager";
import SectionsManager from "./SectionsManager";
import BulkOperations from "./BulkOperations";
import DataExport from "./DataExport";
import LivePricesManager from "./LivePricesManager";

const STORAGE_KEY = "cava-admin-data";

export interface AdminData {
  menuItems: MenuItem[];
  menuConfigs: typeof originalMenuConfigs;
  submenuLabels: Record<string, { en: string; fr: string }>;
  submenuAddOns: typeof originalSubmenuAddOns;
  submenuInfos: typeof originalSubmenuInfos;
  recipes: Record<string, Recipe>;
}

function loadFromStorage(): AdminData | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return null;
}

function saveToStorage(data: AdminData): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function getInitialData(): AdminData {
  return {
    menuItems: JSON.parse(JSON.stringify(originalMenuItems)),
    menuConfigs: JSON.parse(JSON.stringify(originalMenuConfigs)),
    submenuLabels: JSON.parse(JSON.stringify(originalSubmenuLabels)),
    submenuAddOns: JSON.parse(JSON.stringify(originalSubmenuAddOns)),
    submenuInfos: JSON.parse(JSON.stringify(originalSubmenuInfos)),
    recipes: JSON.parse(JSON.stringify(originalRecipeBook)),
  };
}

type TabId = "prices" | "items" | "recipes" | "sections" | "bulk" | "export";

interface NavItem {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  legacy?: boolean;
  breadcrumb: string;
  title: string;
}

const NAV: NavItem[] = [
  {
    id: "prices",
    label: "Live Prices",
    breadcrumb: "Catalog",
    title: "Live Prices",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
      </svg>
    ),
  },
  {
    id: "items",
    label: "Menu Items",
    legacy: true,
    breadcrumb: "Legacy tools",
    title: "Menu Items (legacy)",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
      </svg>
    ),
  },
  {
    id: "recipes",
    label: "Recipes",
    legacy: true,
    breadcrumb: "Legacy tools",
    title: "Recipes (legacy)",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
      </svg>
    ),
  },
  {
    id: "sections",
    label: "Structure",
    legacy: true,
    breadcrumb: "Legacy tools",
    title: "Structure (legacy)",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
      </svg>
    ),
  },
  {
    id: "bulk",
    label: "Bulk Ops",
    legacy: true,
    breadcrumb: "Legacy tools",
    title: "Bulk Ops (legacy)",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  },
  {
    id: "export",
    label: "Export",
    legacy: true,
    breadcrumb: "Legacy tools",
    title: "Export (legacy)",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    ),
  },
];

interface AdminLayoutProps {
  onLogout: () => void;
  userEmail?: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ onLogout, userEmail }) => {
  const [activeTab, setActiveTab] = useState<TabId>("prices");
  const [data, setData] = useState<AdminData>(() => {
    return loadFromStorage() || getInitialData();
  });
  const [hasChanges, setHasChanges] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) !== null;
  });

  const updateData = useCallback((updater: (prev: AdminData) => AdminData) => {
    setData((prev) => {
      const next = updater(prev);
      saveToStorage(next);
      setHasChanges(true);
      return next;
    });
  }, []);

  const resetData = useCallback(() => {
    const fresh = getInitialData();
    setData(fresh);
    localStorage.removeItem(STORAGE_KEY);
    setHasChanges(false);
  }, []);

  const liveNav = NAV.filter((n) => !n.legacy);
  const legacyNav = NAV.filter((n) => n.legacy);
  const activeNav = NAV.find((n) => n.id === activeTab) ?? NAV[0];
  const userInitial = (userEmail || "?").charAt(0).toUpperCase();

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-brand">
          <div className="admin-sidebar-brand-logo">C</div>
          <div className="admin-sidebar-brand-text">
            <span className="name">Cava</span>
            <span className="sub">Menu Studio</span>
          </div>
        </div>

        <nav className="admin-sidebar-nav" aria-label="Admin">
          <div className="admin-sidebar-group-label">Live</div>
          {liveNav.map((n) => (
            <button
              key={n.id}
              className={`admin-sidebar-nav-item ${activeTab === n.id ? "active" : ""}`}
              onClick={() => setActiveTab(n.id)}
            >
              <span className="icon">{n.icon}</span>
              <span>{n.label}</span>
            </button>
          ))}

          <div className="admin-sidebar-group-label" style={{ marginTop: "0.5rem" }}>
            Legacy tools
          </div>
          {legacyNav.map((n) => (
            <button
              key={n.id}
              className={`admin-sidebar-nav-item ${activeTab === n.id ? "active" : ""}`}
              onClick={() => setActiveTab(n.id)}
            >
              <span className="icon">{n.icon}</span>
              <span>{n.label}</span>
              <span className="legacy-tag">v1</span>
            </button>
          ))}
        </nav>

        <div className="admin-sidebar-user">
          <div className="admin-sidebar-user-avatar">{userInitial}</div>
          <div className="admin-sidebar-user-info">
            <div className="admin-sidebar-user-email">{userEmail || "—"}</div>
            <div className="admin-sidebar-user-role">Admin</div>
          </div>
          <button className="admin-sidebar-logout" onClick={onLogout}>
            Logout
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <header className="admin-topbar">
          <div className="admin-topbar-title">
            <div className="crumbs">{activeNav.breadcrumb}</div>
            <h1>{activeNav.title}</h1>
          </div>
          <div className="admin-topbar-actions">
            {activeNav.legacy && hasChanges && (
              <span className="admin-pill" style={{ background: "#fffbeb", color: "#d97706" }}>
                <span className="dot" />
                Unsaved local changes
              </span>
            )}
            {activeNav.legacy && (
              <button className="admin-btn admin-btn-ghost" onClick={resetData}>
                Reset local
              </button>
            )}
            {!activeNav.legacy && (
              <span className="admin-pill">
                <span className="dot" />
                Connected to Supabase
              </span>
            )}
          </div>
        </header>

        <main className="admin-content">
          {activeTab === "prices" && <LivePricesManager />}
          {activeTab === "items" && (
            <MenuItemsManager data={data} updateData={updateData} />
          )}
          {activeTab === "recipes" && (
            <RecipesManager data={data} updateData={updateData} />
          )}
          {activeTab === "sections" && (
            <SectionsManager data={data} updateData={updateData} />
          )}
          {activeTab === "bulk" && (
            <BulkOperations data={data} updateData={updateData} />
          )}
          {activeTab === "export" && (
            <DataExport data={data} resetData={resetData} />
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
